import * as React from 'react';
import Box, { type BoxProps } from '@mui/material/Box';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { cn } from '@/lib/utils';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';

export type TagVariant =
  | 'default'
  | 'outlined'
  | 'selected'
  | 'disabled'
  | 'danger'
  | 'warning'
  | 'success'
  | 'info'
  | 'neutral';

export type TagSize = 'xs' | 'sm' | 'md';

const tagVariantClassNames: Record<TagVariant, string> = {
  default: 'border-transparent bg-[#E9F7F7] text-[#147B78]',
  outlined: 'border-[#D9E9F1] bg-white text-[#0E7490]',
  selected: 'border-[#20AEDC] bg-[#EAFBFF] text-[#0E7490]',
  disabled: 'border-[#E5E7EB] bg-[#F3F4F6] text-[#98A2B3]',
  danger: 'border-[#FECACA] bg-[#FEF2F2] text-[#B42318]',
  warning: 'border-[#FED7AA] bg-[#FFF7ED] text-[#B45309]',
  success: 'border-[#BDE9D6] bg-[#EEFBF5] text-[#167A55]',
  info: 'border-[#BFDBFE] bg-[#EFF6FF] text-[#2563EB]',
  neutral: 'border-[#E5E7EB] bg-[#F9FAFB] text-[#4B5563]',
};

const tagSizeClassNames: Record<TagSize, string> = {
  xs: 'min-h-5 px-2 py-0.5 text-[11px] leading-[14px]',
  sm: 'min-h-6 px-2.5 py-0.5 text-xs leading-4',
  md: 'min-h-7 px-3 py-1 text-sm leading-[18px]',
};

export interface TagProps extends Omit<BoxProps, 'children' | 'component' | 'onClick'> {
  children?: React.ReactNode;
  label?: React.ReactNode;
  variant?: TagVariant;
  size?: TagSize;
  selected?: boolean;
  disabled?: boolean;
  maxWidth?: number | string;
  truncate?: boolean;
  startIcon?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onRemove?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  removeAriaLabel?: string;
}

export const Tag = React.forwardRef<HTMLElement, TagProps>(
  (
    {
      children,
      label,
      variant = 'default',
      size = 'sm',
      selected = false,
      disabled = false,
      maxWidth,
      truncate = true,
      startIcon,
      onClick,
      onRemove,
      removeAriaLabel,
      className,
      sx,
      title,
      ...props
    },
    ref,
  ) => {
    const content = label ?? children;
    const displayContent = typeof content === 'string' ? formatUnderscoreDisplayLabel(content) : content;
    const isInteractive = Boolean(onClick);
    const resolvedVariant: TagVariant = disabled ? 'disabled' : selected ? 'selected' : variant;
    const fallbackTitle = typeof displayContent === 'string' ? displayContent : undefined;
    const ariaRemoveLabel = removeAriaLabel ?? (fallbackTitle ? `Remove ${fallbackTitle}` : 'Remove tag');

    return (
      <Box
        ref={ref}
        component={isInteractive ? 'button' : 'span'}
        type={isInteractive ? 'button' : undefined}
        disabled={isInteractive ? disabled : undefined}
        aria-disabled={!isInteractive && disabled ? true : undefined}
        title={title ?? fallbackTitle}
        className={cn(
          'inline-flex max-w-full min-w-0 items-center gap-1 rounded-full border font-semibold tracking-normal transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FD6EC] focus-visible:ring-offset-1',
          tagVariantClassNames[resolvedVariant],
          tagSizeClassNames[size],
          isInteractive && !disabled ? 'cursor-pointer hover:border-[#20AEDC] hover:bg-[#F0FBFF]' : '',
          disabled ? 'cursor-not-allowed opacity-80' : '',
          className,
        )}
        sx={[
          maxWidth != null ? { maxWidth } : {},
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        onClick={isInteractive && !disabled ? onClick : undefined}
        {...props}
      >
        {startIcon ? (
          <span className="inline-flex shrink-0 items-center text-current" aria-hidden="true">
            {startIcon}
          </span>
        ) : null}
        <span
          className={cn(
            'min-w-0',
            truncate ? 'overflow-hidden text-ellipsis whitespace-nowrap' : 'whitespace-normal break-words',
          )}
        >
          {displayContent}
        </span>
        {onRemove ? (
          <button
            type="button"
            aria-label={ariaRemoveLabel}
            disabled={disabled}
            className="ml-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-current opacity-70 transition-opacity hover:bg-black/5 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FD6EC]"
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.stopPropagation();
              onRemove(event);
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 14 }} />
          </button>
        ) : null}
      </Box>
    );
  },
);

Tag.displayName = 'Tag';

export interface TagListProps extends React.HTMLAttributes<HTMLDivElement> {
  gap?: 'xs' | 'sm' | 'md';
}

const tagListGapClassNames: Record<NonNullable<TagListProps['gap']>, string> = {
  xs: 'gap-1',
  sm: 'gap-1.5',
  md: 'gap-2',
};

export const TagList = React.forwardRef<HTMLDivElement, TagListProps>(
  ({ className, gap = 'sm', ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex min-w-0 flex-wrap items-center', tagListGapClassNames[gap], className)}
      {...props}
    />
  ),
);

TagList.displayName = 'TagList';
